import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [recentBroadcasts, setRecentBroadcasts] = useState([]);

  useEffect(() => {
    api.get('/analytics/overview').then(r => setOverview(r.data)).catch(console.error);
    api.get('/analytics/growth').then(r => setGrowth(r.data)).catch(console.error);
    api.get('/analytics/broadcasts').then(r => setRecentBroadcasts(r.data.slice(0, 5))).catch(console.error);
  }, []);

  const firstName = user?.name?.split(' ')[0] || 'there';
  const today = new Intl.DateTimeFormat('en', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  const setup = [
    { done: !!user?.wa_config?.connected, label: 'Connect your WhatsApp number', to: '/settings' },
    { done: (overview?.total_contacts || 0) > 0, label: 'Add your first customers', to: '/contacts' },
    { done: (overview?.total_broadcasts || 0) > 0, label: 'Send your first broadcast', to: '/broadcasts/new' },
  ];
  const completedSetup = setup.filter(item => item.done).length;

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div>
          <div className="dashboard-date">{today}</div>
          <h1>Good morning, {firstName}.</h1>
          <p>Here is what is happening across your customer relationships.</p>
        </div>
        <div className="dashboard-actions">
          <Link to="/contacts" className="btn btn-ghost">Add customer</Link>
          <Link to="/broadcasts/new" className="btn btn-primary">New broadcast <span aria-hidden="true">↗</span></Link>
        </div>
      </header>

      <section className="dashboard-metrics" aria-label="Headline performance">
        <MetricCard icon="people" label="Total customers" value={overview?.total_contacts} note="Your reachable audience" />
        <MetricCard icon="send" label="Messages sent" value={overview?.total_broadcasts} note="Broadcasts created" />
        <MetricCard icon="check" label="Delivery rate" value={overview && `${overview.delivery_rate ?? 0}%`} note="Reached their phone" tone="success" />
        <MetricCard icon="cursor" label="Click-through rate" value={overview && `${overview.ctr ?? 0}%`} note={`${overview?.total_clicks ?? 0} total link clicks`} />
      </section>

      <section className="dashboard-secondary" aria-label="Secondary performance">
        <SecondaryMetric label="Happy to hear from you" value={overview?.opted_in} />
        <SecondaryMetric label="Read on WhatsApp" value={overview && `${overview.open_rate ?? 0}%`} />
        <SecondaryMetric label="Asked to stop" value={overview?.opted_out} />
        <SecondaryMetric label="Total link clicks" value={overview?.total_clicks} />
      </section>

      <div className="dashboard-main-grid">
        <section className="card dashboard-chart-card">
          <div className="dashboard-card-head">
            <div><span className="dashboard-card-kicker">Audience</span><h2>Customer growth</h2></div>
            <Link to="/analytics">View analytics →</Link>
          </div>
          {growth.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={growth} margin={{ top: 16, right: 8, left: -20, bottom: 0 }}>
                <defs><linearGradient id="customerGrowth" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#35e08f" stopOpacity={0.28} /><stop offset="95%" stopColor="#35e08f" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }} />
                <Area type="monotone" dataKey="count" stroke="#35e08f" fill="url(#customerGrowth)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState title="Your audience story starts here" description="Add your first customers and this view will track how your list grows." action="Go to Contacts" to="/contacts" />}
        </section>

        <aside className="card dashboard-setup-card">
          <div className="dashboard-card-head">
            <div><span className="dashboard-card-kicker">Quick start</span><h2>Set up your workspace</h2></div>
            <span className="setup-count">{completedSetup}/{setup.length}</span>
          </div>
          <div className="setup-progress"><span style={{ width: `${(completedSetup / setup.length) * 100}%` }} /></div>
          <div className="setup-list">
            {setup.map((item, index) => (
              <Link to={item.to} className={`setup-item${item.done ? ' done' : ''}`} key={item.label}>
                <span className="setup-check">{item.done ? '✓' : index + 1}</span><span>{item.label}</span><span aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
          <p className="setup-footnote">Complete these steps once and TuWay will take care of the daily rhythm.</p>
        </aside>
      </div>

      <section className="card dashboard-activity-card">
        <div className="dashboard-card-head">
          <div><span className="dashboard-card-kicker">Recent activity</span><h2>Latest broadcasts</h2></div>
          <Link to="/broadcasts">View all →</Link>
        </div>
        {recentBroadcasts.length === 0 ? (
          <EmptyState title="Nothing sent yet" description="Your recent broadcasts and their performance will appear here." action="Send your first broadcast" to="/broadcasts/new" />
        ) : (
          <div className="dashboard-activity-list">
            {recentBroadcasts.map(b => (
              <Link to={`/broadcasts/${b._id}`} className="dashboard-activity-row" key={b._id}>
                <span className={`activity-channel ${b.channel}`}>{b.channel === 'whatsapp' ? 'WA' : 'EM'}</span>
                <span className="activity-name"><strong>{b.name}</strong><small>Sent to {b.total_count} {b.total_count === 1 ? 'person' : 'people'}</small></span>
                <span className="activity-read"><strong>{b.stats?.read ?? 0}</strong><small>read</small></span>
                <StatusBadge status={b.status} /><span className="activity-arrow" aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MetricCard({ icon, label, value, note, tone }) {
  const loading = value === null || value === undefined;
  return <article className={`dashboard-metric-card${tone ? ` ${tone}` : ''}`}><div className="metric-card-top"><MetricIcon name={icon} /><span>{label}</span></div>{loading ? <div className="skeleton skeleton-value" aria-hidden="true" /> : <strong>{value}</strong>}<small>{note}</small></article>;
}

function SecondaryMetric({ label, value }) {
  return <div><span>{label}</span>{value === null || value === undefined ? <i className="skeleton" /> : <strong>{value}</strong>}</div>;
}

function MetricIcon({ name }) {
  const paths = {
    people: <><circle cx="9" cy="8" r="3" /><path d="M3 20v-2a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v2M16 5a3 3 0 0 1 0 6M17 14a4 4 0 0 1 4 4v2" /></>,
    send: <><path d="m3 11 18-8-8 18-2-8-8-2Z" /><path d="m11 13 5-5" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></>,
    cursor: <><path d="m5 3 14 9-7 1-3 7-4-17Z" /></>,
  };
  return <span className="metric-icon" aria-hidden="true"><svg viewBox="0 0 24 24">{paths[name]}</svg></span>;
}

function EmptyState({ title, description, action, to }) {
  return <div className="dashboard-empty"><span>↗</span><strong>{title}</strong><p>{description}</p><Link to={to}>{action} →</Link></div>;
}

function StatusBadge({ status }) {
  const map = { draft: 'badge-gray', sending: 'badge-yellow', sent: 'badge-green', failed: 'badge-red' };
  return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
}
